import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api, uploadMedia, deleteUploadedMedia, timeAgo, resolveMediaUrl } from '../../lib/api';
import { useApp } from '../../context/AppContext';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Dialog, DialogContent } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Plus, X, Send, ChevronLeft, ChevronRight, Heart, Flame, Zap, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';

const REACTIONS = [
  { value: 'heart', icon: Heart, label: 'Like' },
  { value: 'fire', icon: Flame, label: 'Fire' },
  { value: 'energy', icon: Zap, label: 'Energy' },
];
const STORY_SECONDS = 6;

function StoryRing({ children, seen = false, sponsored = false }) {
  return (
    <div className={sponsored ? 'story-ring-muted' : seen ? 'story-ring-muted' : 'story-ring'}>
      <div className="story-ring-inner">{children}</div>
    </div>
  );
}

function StoryViewer({ group, ad, onClose, onNextGroup, onPrevGroup, onDeleted }) {
  const { t, user } = useApp();
  const [idx, setIdx] = useState(0);
  const [reply, setReply] = useState('');
  const [viewerCount, setViewerCount] = useState(0);
  const isAd = !!ad;
  const stories = group?.stories || [];
  const story = stories[idx];

  useEffect(() => {
    if (isAd || !story?.id) return;
    setViewerCount(story.viewers?.length || 0);
    if (story.user_id !== user?.id) {
      api.post(`/stories/${story.id}/view`)
        .then((response) => setViewerCount(response.data.viewer_count))
        .catch(() => {});
    }
  }, [isAd, story?.id, story?.user_id, story?.viewers?.length, user?.id]);

  useEffect(() => {
    if (isAd && ad?.id) api.post(`/ads/${ad.id}/impression`).catch(() => {});
  }, [isAd, ad?.id]);

  useEffect(() => {
    const timer = setTimeout(() => next(), STORY_SECONDS * 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, group, ad]);

  const next = () => {
    if (!isAd && idx < stories.length - 1) setIdx(idx + 1);
    else onNextGroup();
  };
  const prev = () => {
    if (!isAd && idx > 0) setIdx(idx - 1);
    else onPrevGroup();
  };

  const react = async (reaction) => {
    if (isAd) return;
    try {
      await api.post(`/stories/${story.id}/react`, { emoji: reaction });
      toast.success(t('reactionSent'));
    } catch { toast.error(t('failed')); }
  };

  const sendReply = async () => {
    if (!reply.trim() || isAd) return;
    try {
      await api.post(`/stories/${story.id}/reply`, { text: reply });
      toast.success(t('send') + ' ✓');
      setReply('');
    } catch { toast.error(t('failed')); }
  };

  const deleteStory = async () => {
    if (!story?.id || !window.confirm(t('deleteStoryConfirm'))) return;
    try {
      await api.delete(`/stories/${story.id}`);
      toast.success(t('storyDeleted'));
      onDeleted();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('connectionError'));
    }
  };

  const image = isAd ? ad.image : story?.image;
  const isVideo = !isAd && story?.media_type === 'video';
  const caption = isAd ? ad.title : story?.caption;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="p-0 w-screen h-[100dvh] max-w-full sm:max-w-md sm:h-[85vh] bg-black border-0 sm:border sm:border-border overflow-hidden rounded-none sm:rounded-xl" aria-describedby={undefined}>
        <div className="relative w-full h-full" data-testid={isAd ? 'sponsored-story' : 'story-viewer'}>
          {/* progress bars */}
          <div className="absolute top-3 inset-x-3 z-20 flex gap-1">
            {(isAd ? [0] : stories).map((_, i) => (
              <div key={i} className="flex-1 h-0.5 rounded-full bg-white/30 overflow-hidden">
                {i < idx ? (
                  <div className="h-full bg-white w-full" />
                ) : i === idx ? (
                  <div key={`fill-${idx}`} className="h-full bg-white w-full story-progress-fill" style={{ animationDuration: `${STORY_SECONDS}s` }} />
                ) : null}
              </div>
            ))}
          </div>
          {/* header */}
          <div className="absolute top-6 inset-x-3 z-20 flex items-center gap-2">
            {!isAd && (
              <>
                <Avatar className="w-8 h-8 ring-1 ring-white/40">
                  <AvatarImage src={resolveMediaUrl(group.user?.avatar)} />
                  <AvatarFallback>{group.user?.username?.[0]}</AvatarFallback>
                </Avatar>
                <span className="text-white text-sm font-medium drop-shadow" dir="ltr">@{group.user?.username}</span>
                <span className="text-white/60 text-xs">{timeAgo(story?.created_at)}</span>
              </>
            )}
            {isAd && (
              <span className="text-[10px] uppercase tracking-wider text-white/90 border border-white/40 rounded-full px-2 py-0.5">
                {t('sponsored')} · {ad.sponsor}
              </span>
            )}
            <button onClick={onClose} className="ms-auto p-1.5 rounded-full bg-black/40 text-white" data-testid="story-close-button" aria-label="close">
              <X size={16} />
            </button>
            {!isAd && story?.user_id === user?.id && (
              <button onClick={deleteStory} className="p-1.5 rounded-full bg-black/40 text-white" data-testid="story-delete-button" aria-label={t('delete')}>
                <Trash2 size={16} />
              </button>
            )}
          </div>
          {/* story media */}
          {isVideo ? (
            <video src={resolveMediaUrl(image)} className="w-full h-full object-cover" autoPlay muted playsInline loop />
          ) : (
            <img src={resolveMediaUrl(image)} alt="story" className="w-full h-full object-cover" />
          )}
          <div className="absolute inset-x-0 bottom-0 h-1/2 reels-vignette pointer-events-none" />
          {/* nav zones */}
          <button onClick={prev} aria-label="previous" className="absolute inset-y-0 start-0 w-1/4 z-10 flex items-center justify-start ps-1 text-white/0 hover:text-white/60"><ChevronLeft className="rtl:rotate-180" /></button>
          <button onClick={next} aria-label="next" className="absolute inset-y-0 end-0 w-1/4 z-10 flex items-center justify-end pe-1 text-white/0 hover:text-white/60"><ChevronRight className="rtl:rotate-180" /></button>
          {/* caption + actions */}
          <div className="absolute bottom-0 inset-x-0 z-20 p-4 space-y-3 pb-[max(env(safe-area-inset-bottom),1rem)]">
            {caption && <p className="text-white text-sm font-medium drop-shadow">{caption}</p>}
            {isAd ? (
              <Button data-testid="sponsored-story-swipe-cta" onClick={() => api.post(`/ads/${ad.id}/click`).catch(() => {})}
                className="w-full font-semibold">
                {ad.cta}
              </Button>
            ) : group.user?.id !== user?.id ? (
              <>
                <div className="flex gap-3 justify-center">
                  {REACTIONS.map(({ value, icon: Icon, label }) => (
                    <button key={value} data-testid={`story-reaction-${value}`} onClick={() => react(value)} aria-label={label}
                      className="w-10 h-10 rounded-full bg-card/20 border border-white/30 flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"><Icon size={18} /></button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input data-testid="story-viewer-reply-input" value={reply} onChange={(e) => setReply(e.target.value)}
                    placeholder={`${t('reply')}...`} className="bg-black/50 border-white/30 text-white placeholder:text-white/50 h-10 rounded-full px-4"
                    onKeyDown={(e) => e.key === 'Enter' && sendReply()} />
                  <Button data-testid="story-viewer-send-reply-button" size="sm" className="h-10 rounded-full" onClick={sendReply}>
                    <Send size={14} />
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-white/70 text-xs text-center flex items-center justify-center gap-3">
                <span>{story?.reactions?.length || 0} {t('reactions')}</span>
                <span className="inline-flex items-center gap-1"><Eye size={13} />{viewerCount} {t('views')}</span>
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function StoriesBar() {
  const { user, t } = useApp();
  const [groups, setGroups] = useState([]);
  const [storyAds, setStoryAds] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreview, setPendingPreview] = useState('');
  const [caption, setCaption] = useState('');
  const [publishing, setPublishing] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async () => {
    try {
      const [sRes, aRes] = await Promise.all([api.get('/stories'), api.get('/ads?placement=story')]);
      setGroups(sRes.data);
      setStoryAds(aRes.data);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const sequence = [];
  groups.forEach((g, i) => {
    sequence.push({ type: 'group', group: g });
    if ((i + 1) % 3 === 0 && storyAds.length > 0) {
      sequence.push({ type: 'ad', ad: storyAds[Math.floor(i / 3) % storyAds.length] });
    }
  });

  const openAt = (seqIndex) => setViewing(seqIndex);
  const closeViewer = () => setViewing(null);
  const nextGroup = () => {
    if (viewing === null) return;
    if (viewing < sequence.length - 1) setViewing(viewing + 1);
    else closeViewer();
  };
  const prevGroup = () => {
    if (viewing === null) return;
    if (viewing > 0) setViewing(viewing - 1);
  };

  const chooseStory = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/') && !f.type.startsWith('video/')) {
      toast.error(t('selectImageVideo'));
      e.target.value = '';
      return;
    }
    if (f.size > 100 * 1024 * 1024) {
      toast.error(t('storyTooLarge'));
      e.target.value = '';
      return;
    }
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingFile(f);
    setPendingPreview(URL.createObjectURL(f));
    e.target.value = '';
  };

  const closeCreate = () => {
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingFile(null);
    setPendingPreview('');
    setCaption('');
  };

  const createStory = async () => {
    if (!pendingFile) return;
    setPublishing(true);
    let uploadedUrl = '';
    try {
      const media = await uploadMedia(pendingFile);
      uploadedUrl = media.url;
      await api.post('/stories', {
        image: media.url,
        caption: caption.trim(),
        media_type: pendingFile.type.startsWith('video/') ? 'video' : 'image',
      });
      toast.success(t('storyLive'));
      closeCreate();
      load();
    } catch {
      if (uploadedUrl) await deleteUploadedMedia(uploadedUrl).catch(() => {});
      toast.error(t('failed'));
    }
    finally { setPublishing(false); }
  };

  const current = viewing !== null ? sequence[viewing] : null;

  return (
    <div className="py-2" data-testid="stories-rail">
      <div className="flex gap-4 overflow-x-auto px-3 pb-1" style={{ scrollbarWidth: 'none' }}>
        {/* your story */}
        <button data-testid="story-create-button" onClick={() => fileRef.current?.click()} className="flex flex-col items-center gap-1.5 shrink-0 w-[68px]">
          <div className="relative">
            <StoryRing seen>
              <Avatar className="w-14 h-14">
                <AvatarImage src={user?.avatar} />
                <AvatarFallback>{user?.username?.[0]}</AvatarFallback>
              </Avatar>
            </StoryRing>
            <div className="absolute -bottom-0.5 -end-0.5 w-5 h-5 rounded-full bg-foreground text-background flex items-center justify-center border-2 border-background">
              <Plus size={12} />
            </div>
          </div>
          <span className="text-[11px] text-muted-foreground truncate w-full text-center">{t('yourStory')}</span>
        </button>
        <input ref={fileRef} type="file" accept="image/*,video/*" hidden onChange={chooseStory} />
        {/* stories + ads */}
        {sequence.map((item, i) => (
          <button key={i} data-testid="story-item" onClick={() => openAt(i)} className="flex flex-col items-center gap-1.5 shrink-0 w-[68px]">
            <StoryRing sponsored={item.type === 'ad'}>
              <Avatar className="w-14 h-14">
                <AvatarImage src={item.type === 'ad' ? item.ad.image : item.group.user?.avatar} className="object-cover" />
                <AvatarFallback>{item.type === 'ad' ? 'AD' : item.group.user?.username?.[0]}</AvatarFallback>
              </Avatar>
            </StoryRing>
            <span className="text-[11px] text-muted-foreground truncate w-full text-center" dir="ltr">
              {item.type === 'ad' ? t('sponsored') : item.group.user?.username}
            </span>
          </button>
        ))}
      </div>
      {current && (
        <StoryViewer
          group={current.type === 'group' ? current.group : null}
          ad={current.type === 'ad' ? current.ad : null}
          onClose={closeViewer}
          onNextGroup={nextGroup}
          onPrevGroup={prevGroup}
          onDeleted={() => { closeViewer(); load(); }}
        />
      )}
      <Dialog open={!!pendingFile} onOpenChange={(open) => { if (!open && !publishing) closeCreate(); }}>
        <DialogContent className="max-w-sm" aria-describedby={undefined}>
          <div className="overflow-hidden rounded-xl bg-black">
            {pendingFile?.type.startsWith('video/') ? (
              <video src={pendingPreview} className="w-full max-h-[55vh] object-contain" controls muted playsInline />
            ) : (
              <img src={pendingPreview} alt="story preview" className="w-full max-h-[55vh] object-contain" />
            )}
          </div>
          <Input data-testid="story-caption-input" value={caption} onChange={(event) => setCaption(event.target.value)} placeholder={t('storyCaption')} maxLength={150} />
          <Button data-testid="story-publish-button" onClick={createStory} disabled={publishing} className="w-full">
            {publishing ? t('loading') : t('publish')}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
